import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import AdminSidebar from "../components/AdminSidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Settings,
  Save,
  RefreshCw,
  Mail,
  Bell,
  Shield,
  Database,
  Globe,
  Clock,
  AlertTriangle,
  CheckCircle,
  Server,
  Zap
} from "lucide-react";

const AdminSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    platform: {
      site_name: "Rekrutor",
      site_description: "AI-Powered Recruitment Platform",
      maintenance_mode: false,
      registration_enabled: true,
      require_email_verification: true
    },
    notifications: {
      email_notifications_enabled: true,
      push_notifications_enabled: true,
      digest_frequency: "daily",
      admin_alerts_email: ""
    },
    moderation: {
      auto_flag_threshold: 3,
      require_job_approval: false,
      profanity_filter_enabled: true
    },
    limits: {
      max_applications_per_day: 50,
      max_messages_per_day: 100,
      max_job_posts_free: 3,
      trial_duration_days: 14
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/settings`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success("Settings saved successfully");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                PLATFORM <span className="text-crimson">SETTINGS</span>
              </h1>
              <p className="text-gray-400">Configure platform-wide settings</p>
            </div>
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-crimson hover:bg-crimson-hover text-white"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>

          <div className="space-y-6">
            {/* Platform Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-crimson" />
                Platform Settings
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm">Site Name</Label>
                  <Input
                    value={settings.platform.site_name}
                    onChange={(e) => updateSetting("platform", "site_name", e.target.value)}
                    className="bg-obsidian border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Site Description</Label>
                  <Input
                    value={settings.platform.site_description}
                    onChange={(e) => updateSetting("platform", "site_description", e.target.value)}
                    className="bg-obsidian border-[#333] text-white mt-1"
                  />
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.platform.maintenance_mode}
                    onChange={(e) => updateSetting("platform", "maintenance_mode", e.target.checked)}
                    className="w-4 h-4 accent-crimson"
                  />
                  <span className="text-gray-300">Maintenance Mode</span>
                  {settings.platform.maintenance_mode && (
                    <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs">Active</span>
                  )}
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.platform.registration_enabled}
                    onChange={(e) => updateSetting("platform", "registration_enabled", e.target.checked)}
                    className="w-4 h-4 accent-crimson"
                  />
                  <span className="text-gray-300">Allow New Registrations</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.platform.require_email_verification}
                    onChange={(e) => updateSetting("platform", "require_email_verification", e.target.checked)}
                    className="w-4 h-4 accent-crimson"
                  />
                  <span className="text-gray-300">Require Email Verification</span>
                </label>
              </div>
            </motion.div>

            {/* Notification Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-crimson" />
                Notification Settings
              </h2>

              <div className="space-y-3 mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.notifications.email_notifications_enabled}
                    onChange={(e) => updateSetting("notifications", "email_notifications_enabled", e.target.checked)}
                    className="w-4 h-4 accent-crimson"
                  />
                  <span className="text-gray-300">Email Notifications</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.notifications.push_notifications_enabled}
                    onChange={(e) => updateSetting("notifications", "push_notifications_enabled", e.target.checked)}
                    className="w-4 h-4 accent-crimson"
                  />
                  <span className="text-gray-300">Push Notifications</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm">Digest Frequency</Label>
                  <select
                    value={settings.notifications.digest_frequency}
                    onChange={(e) => updateSetting("notifications", "digest_frequency", e.target.value)}
                    className="w-full mt-1 px-3 py-2 bg-obsidian border border-[#333] text-white"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="never">Never</option>
                  </select>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Admin Alerts Email</Label>
                  <Input
                    type="email"
                    value={settings.notifications.admin_alerts_email}
                    onChange={(e) => updateSetting("notifications", "admin_alerts_email", e.target.value)}
                    placeholder="admin@example.com"
                    className="bg-obsidian border-[#333] text-white mt-1"
                  />
                </div>
              </div>
            </motion.div>

            {/* Moderation Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-crimson" />
                Moderation Settings
              </h2>

              <div className="space-y-3 mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.moderation.require_job_approval}
                    onChange={(e) => updateSetting("moderation", "require_job_approval", e.target.checked)}
                    className="w-4 h-4 accent-crimson"
                  />
                  <span className="text-gray-300">Require Admin Approval for Job Posts</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.moderation.profanity_filter_enabled}
                    onChange={(e) => updateSetting("moderation", "profanity_filter_enabled", e.target.checked)}
                    className="w-4 h-4 accent-crimson"
                  />
                  <span className="text-gray-300">Profanity Filter</span>
                </label>
              </div>

              <div>
                <Label className="text-gray-400 text-sm">Auto-flag Threshold (reports)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={settings.moderation.auto_flag_threshold}
                  onChange={(e) => updateSetting("moderation", "auto_flag_threshold", parseInt(e.target.value))}
                  className="bg-obsidian border-[#333] text-white mt-1 w-32"
                />
                <p className="text-gray-500 text-xs mt-1">Content auto-flagged after this many reports</p>
              </div>
            </motion.div>

            {/* Limits Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-crimson" />
                Usage Limits
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm">Max Applications/Day</Label>
                  <Input
                    type="number"
                    min="1"
                    value={settings.limits.max_applications_per_day}
                    onChange={(e) => updateSetting("limits", "max_applications_per_day", parseInt(e.target.value))}
                    className="bg-obsidian border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Max Messages/Day</Label>
                  <Input
                    type="number"
                    min="1"
                    value={settings.limits.max_messages_per_day}
                    onChange={(e) => updateSetting("limits", "max_messages_per_day", parseInt(e.target.value))}
                    className="bg-obsidian border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Free Job Posts Limit</Label>
                  <Input
                    type="number"
                    min="0"
                    value={settings.limits.max_job_posts_free}
                    onChange={(e) => updateSetting("limits", "max_job_posts_free", parseInt(e.target.value))}
                    className="bg-obsidian border-[#333] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Trial Duration (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={settings.limits.trial_duration_days}
                    onChange={(e) => updateSetting("limits", "trial_duration_days", parseInt(e.target.value))}
                    className="bg-obsidian border-[#333] text-white mt-1"
                  />
                </div>
              </div>
            </motion.div>

            {/* System Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Server className="w-5 h-5 text-crimson" />
                System Status
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-gray-300 text-sm">API</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-gray-300 text-sm">Database</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-gray-300 text-sm">Storage</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-gray-300 text-sm">AI Services</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;
